import { DocRankerConditionInput } from '@ui/features/Datasets/components/DatasetForm/components/fields';
import { DatePicker, Form, Input, Radio, Select, Slider, Tooltip, Tree } from 'antd';
import cs from 'classnames';
import dayjs, { type Dayjs } from 'dayjs';
import { logger } from '@ui/utils/logger';
import React from 'react';
import {
    useFilterGroupsActions,
    useFilterMetadata,
    useFilterOptions,
    useMapDefaultOperator,
    useMultiSelectActions,
    useMultiSelectValues,
} from '../hooks';
import { FilterInputType } from '../types/filter-metadata.types';
import type { ConditionOperator, FilterCondition } from '../types/filter-panel.types';
import { MultiSelectWithSearch } from './MultiSelectWithSearch';
import Styles from './Styles.module.scss';

interface ConditionValueInputProps {
    groupId: string;
    condition: FilterCondition;
}

export function ConditionValueInput({ groupId, condition }: ConditionValueInputProps) {
    const { metadata, filterValuesMap, handleSearchTerm } = useFilterMetadata();
    const { diagnosisGroupedOptions } = useFilterOptions();
    const { multiSelectInValues, multiSelectOutValues } = useMultiSelectValues();
    const { setMultiSelectInValues, setMultiSelectOutValues } = useMultiSelectActions();
    const { updateCondition } = useFilterGroupsActions();
    const mapDefaultOperator = useMapDefaultOperator();

    const meta = metadata?.filters?.find(f => f.field === condition.field);
    if (!meta) {
        // Fallback to text tags for unknown
        return (
            <Form.Item style={{ marginBottom: 0, flex: 1 }}>
                <Select
                    mode="tags"
                    placeholder="Enter values"
                    className={Styles.customSelect}
                    value={condition.value}
                    onChange={value => updateCondition(groupId, condition.id, { value, operator: 'contains' })}
                />
            </Form.Item>
        );
    }

    if (meta.inputType === FilterInputType.TEXT) {
        // For note_search, provide initial options
        const initialOptions =
            meta.field === 'note_search'
                ? [
                      { label: 'ICD-10', value: 'icd10' },
                      { label: 'e11', value: 'e11' },
                  ]
                : [];

        return (
            <Form.Item style={{ marginBottom: 0, flex: 1 }}>
                <Select
                    placeholder={meta.placeholder || `Search ${meta.label.toLowerCase()}...`}
                    value={condition.value}
                    className={Styles.customSelect}
                    onChange={value => {
                        updateCondition(groupId, condition.id, { value, operator: 'contains' });
                    }}
                    mode="tags"
                    options={initialOptions}
                    allowClear
                />
            </Form.Item>
        );
    }

    if (meta.inputType === FilterInputType.NUMBER_RANGE) {
        const minValue = meta.field === 'age' ? 0 : meta.validation?.min || 0;
        const maxValue = meta.field === 'age' ? 100 : meta.validation?.max || 100;
        const currentValue = condition.value || [minValue, maxValue];

        // Create marks only for age filter (visit_count doesn't need marks)
        const marks =
            meta.field === 'age'
                ? {
                      0: '0',
                      25: '25',
                      50: '50',
                      75: '75',
                      100: '100',
                  }
                : undefined;

        return (
            <Form.Item style={{ marginBottom: 0, flex: 1 }}>
                <div>
                    <Slider
                        range
                        min={minValue}
                        max={maxValue}
                        step={1}
                        value={currentValue}
                        onChange={value => {
                            updateCondition(groupId, condition.id, {
                                value,
                                operator: 'between',
                            });
                        }}
                        className={meta.field === 'age' ? Styles.ageSlider : undefined}
                        marks={marks}
                        tooltip={{ formatter: value => (meta.field === 'age' ? `${value} years` : `${value}`) }}
                    />
                    {meta.field === 'age' && (
                        <div className="mt-2 flex items-center gap-2">
                            <div className="flex-1">
                                <Input
                                    addonBefore="From:"
                                    value={currentValue[0]}
                                    onChange={e => {
                                        const value = parseInt(e.target.value, 10);
                                        if (!isNaN(value) && value >= minValue && value <= maxValue) {
                                            // Ensure "to" value is not less than new "from" value
                                            const newToValue = Math.max(value, currentValue[1]);
                                            updateCondition(groupId, condition.id, {
                                                value: [value, newToValue],
                                                operator: 'between',
                                            });
                                        } else if (e.target.value === '') {
                                            // Allow empty input temporarily
                                            updateCondition(groupId, condition.id, {
                                                value: [minValue, currentValue[1]],
                                                operator: 'between',
                                            });
                                        }
                                    }}
                                    type="number"
                                    min={minValue}
                                    max={maxValue}
                                    className={Styles.ageInput}
                                    style={{ width: '100%' }}
                                />
                            </div>
                            <div className="flex-1">
                                <Input
                                    addonBefore="To:"
                                    value={currentValue[1]}
                                    onChange={e => {
                                        const value = parseInt(e.target.value, 10);
                                        if (
                                            !isNaN(value) &&
                                            value >= minValue &&
                                            value <= maxValue &&
                                            value >= currentValue[0]
                                        ) {
                                            updateCondition(groupId, condition.id, {
                                                value: [currentValue[0], value],
                                                operator: 'between',
                                            });
                                        } else if (e.target.value === '') {
                                            // Allow empty input temporarily
                                            updateCondition(groupId, condition.id, {
                                                value: [currentValue[0], maxValue],
                                                operator: 'between',
                                            });
                                        }
                                    }}
                                    type="number"
                                    min={currentValue[0]}
                                    max={maxValue}
                                    className={Styles.ageInput}
                                    style={{ width: '100%' }}
                                />
                            </div>
                        </div>
                    )}
                </div>
            </Form.Item>
        );
    }

    if (meta.inputType === FilterInputType.SELECT || meta.inputType === FilterInputType.MULTI_SELECT) {
        const values = filterValuesMap[meta.field];

        // Use pre-computed grouped options for diagnosis
        const groupedOptions = meta.field === 'diagnosis' ? diagnosisGroupedOptions : [];

        // Helper to flatten tree structure to options array for diagnosis
        const flattenDiagnosisOptions = (nodes: any[]): any[] => {
            const result: any[] = [];
            nodes.forEach(node => {
                if (node.icdValue) {
                    result.push({
                        label: node.icdLabel || node.title,
                        value: node.icdValue,
                        key: `${node.icdValue}__${node.conceptId}`,
                    });
                }
                if (node.children) {
                    result.push(...flattenDiagnosisOptions(node.children));
                }
            });
            return result;
        };

        const options =
            meta.field === 'diagnosis'
                ? flattenDiagnosisOptions(groupedOptions) // Flatten tree to options array
                : values?.map((v: any, index: number) => {
                      let label = v.label ?? v.value;

                      // Add count if available
                      if (v.count) {
                          label = `${label} (${v.count})`;
                      }

                      const optionValue = v.value ?? v.label;
                      const optionKey = `${optionValue}-${v.concept_id}`;

                      return {
                          label,
                          value: optionValue,
                          key: optionKey,
                      };
                  }) || [];

        const handleSearch = (search: string) => {
            if (!meta.requiresValues) return;
            handleSearchTerm(meta.field, search, () => {
                // Callback is handled by useSearchTerms hook
            });
        };

        // For MULTI_SELECT, show two selectboxes: "In" and "Out"
        if (meta.inputType === FilterInputType.MULTI_SELECT) {
            const conditionKey = `${groupId}-${condition.id}`;
            const inValues = multiSelectInValues[conditionKey] || [];
            const outValues = multiSelectOutValues[conditionKey] || [];

            // Helper to update condition value from IN/OUT arrays
            // newInValues and newOutValues are now {value, title} format
            const updateConditionFromInOut = (newInValues: any[], newOutValues: any[]) => {
                // Extract values from {value, title} format
                const inValueArray = newInValues.map(v =>
                    typeof v === 'object' && v.value !== undefined ? v.value : v
                );
                const outValueArray = newOutValues.map(v =>
                    typeof v === 'object' && v.value !== undefined ? v.value : v
                );
                const allValues = [...inValueArray, ...outValueArray];
                const valueStatuses: Record<string, 'IN' | 'OUT'> = {};
                inValueArray.forEach(val => {
                    valueStatuses[String(val)] = 'IN';
                });
                outValueArray.forEach(val => {
                    valueStatuses[String(val)] = 'OUT';
                });

                // Update condition value
                updateCondition(groupId, condition.id, {
                    value: allValues.length > 0 ? allValues : undefined,
                    operator: mapDefaultOperator(meta.defaultOperator) || 'in',
                });

                // Store valueStatuses in condition (will be used when building AdvancedFilterQuery)
                // We'll handle this in the emit effect
            };

            // Helper to get label for a value
            // First check inValues/outValues for title, then fallback to options or value
            const getLabelForValue = (val: any): string => {
                // Check inValues first
                const inItem = inValues.find((item: any) => {
                    const itemValue = typeof item === 'object' && item.value !== undefined ? item.value : item;
                    return String(itemValue) === String(val);
                });
                if (inItem && typeof inItem === 'object' && inItem.title) {
                    return inItem.title;
                }

                // Check outValues
                const outItem = outValues.find((item: any) => {
                    const itemValue = typeof item === 'object' && item.value !== undefined ? item.value : item;
                    return String(itemValue) === String(val);
                });
                if (outItem && typeof outItem === 'object' && outItem.title) {
                    return outItem.title;
                }

                // Then check options
                const option = options.find((opt: { label: string; value: any }) => opt.value === val);
                if (option) {
                    return option.label;
                }

                // Fallback to value itself
                return String(val);
            };

            // Helper to handle value selection for IN or OUT
            // selectedValues is the complete list of selected values (from Select onChange or Tree onCheck)
            // options is already in scope, no need to pass as parameter
            const handleValueSelect = (selectedValues: any[], isIn: boolean, source: 'select' | 'tree' = 'select') => {
                // Get previous values for this condition key to preserve values not in current options
                const prevValues = isIn ? inValues : outValues;

                // Create a map of existing values with their titles for quick lookup
                const existingValuesMap = new Map<string, { value: any; title: string }>();
                prevValues.forEach((item: any) => {
                    const val = typeof item === 'object' && item.value !== undefined ? item.value : item;
                    const title = typeof item === 'object' && item.title !== undefined ? item.title : String(val);
                    existingValuesMap.set(String(val), { value: val, title });
                });

                // Create selected values set for quick lookup
                const selectedValuesSet = new Set(selectedValues.map(v => String(v)));

                // Build new values list:
                // 1. Include all selected values (with title from options or existing)
                // 2. Include previous values that are not in selectedValues (preserve values not in current options)
                const newValuesWithTitles: Array<{ value: any; title: string }> = [];

                // First, add all selected values
                selectedValues.forEach((val: any) => {
                    // Try to find title from options first
                    const option = options.find((opt: { label: string; value: any }) => opt.value === val);
                    const title = option ? option.label : existingValuesMap.get(String(val))?.title || String(val);
                    newValuesWithTitles.push({ value: val, title });
                });

                // Then, preserve previous values that are not in selectedValues (not in current options)
                // Only do this if the source is 'tree' (because Tree only returns visible checked nodes)
                // If source is 'select' (tag removal), we should respect the removal even if not in options
                if (source === 'tree') {
                    prevValues.forEach((item: any) => {
                        const val = typeof item === 'object' && item.value !== undefined ? item.value : item;
                        if (!selectedValuesSet.has(String(val))) {
                            // Check if this value exists in current options
                            const existsInOptions = options.some(
                                (opt: { label: string; value: any }) => opt.value === val
                            );
                            // Only preserve if it doesn't exist in current options (to keep old search results)
                            if (!existsInOptions) {
                                const title =
                                    typeof item === 'object' && item.title !== undefined ? item.title : String(val);
                                newValuesWithTitles.push({ value: val, title });
                            }
                        }
                    });
                }

                logger.debug('New values with titles', { conditionKey, newValuesWithTitles });

                // Update state
                if (isIn) {
                    setMultiSelectInValues(prev => ({
                        ...prev,
                        [conditionKey]: newValuesWithTitles,
                    }));
                } else {
                    setMultiSelectOutValues(prev => ({
                        ...prev,
                        [conditionKey]: newValuesWithTitles,
                    }));
                }

                // Update condition value with all values (IN + OUT)
                // Get the other side values (from closure, which should be current)
                const otherValues = isIn ? outValues : inValues;
                updateConditionFromInOut(newValuesWithTitles, otherValues);
            };

            // Special handling for diagnosis field with Tree
            if (meta.field === 'diagnosis') {
                // Create disabled tree data for In selectbox (disable items in outValues)
                // disabledValues is now {value, title} format, extract values
                // If all children are disabled, parent should also be disabled
                const createDisabledTreeData = (nodes: any[], disabledValues: any[]): any[] => {
                    if (!nodes || nodes.length === 0) {
                        return [];
                    }

                    const disabledValueArray = disabledValues.map(v =>
                        typeof v === 'object' && v.value !== undefined ? v.value : v
                    );

                    return nodes.map(node => {
                        // Ensure node has required properties
                        if (!node || typeof node !== 'object') {
                            return node;
                        }

                        // First, process children recursively
                        let processedChildren: any[] | undefined;
                        if (node.children && Array.isArray(node.children) && node.children.length > 0) {
                            processedChildren = createDisabledTreeData(node.children, disabledValues);
                            // If processed children is empty but original had children, preserve structure
                            // This ensures tree structure is maintained even if all children are filtered
                            if (processedChildren.length === 0 && node.children.length > 0) {
                                // Keep original children structure to maintain tree visibility
                                processedChildren = node.children;
                            }
                        } else if (node.children && Array.isArray(node.children)) {
                            // Preserve original children structure if empty array
                            processedChildren = node.children;
                        }

                        // Check if this is a leaf node that should be disabled
                        const isLeafDisabled =
                            node.isLeaf === true && node.icdValue && disabledValueArray.includes(node.icdValue);

                        // Check if this is a parent node and all its children are disabled
                        // Only check if it's NOT a leaf and has children
                        const allChildrenDisabled =
                            node.isLeaf !== true &&
                            processedChildren &&
                            Array.isArray(processedChildren) &&
                            processedChildren.length > 0
                                ? processedChildren.every((child: any) => child && child.disabled === true)
                                : false;

                        // Disable if it's a disabled leaf OR if all children are disabled
                        const isDisabled = isLeafDisabled || allChildrenDisabled;

                        return {
                            ...node,
                            disabled: isDisabled,
                            // Always preserve children structure if it exists (even if empty array)
                            // Ant Design Tree needs children array to show expandable nodes
                            children:
                                processedChildren && processedChildren.length > 0
                                    ? processedChildren
                                    : node.children && Array.isArray(node.children)
                                      ? node.children
                                      : undefined,
                        };
                    });
                };

                const inTreeData = createDisabledTreeData(groupedOptions, outValues);
                const outTreeData = createDisabledTreeData(groupedOptions, inValues);

                return (
                    <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 4 }}>
                        {/* In (IN) Selectbox */}
                        <div className="flex items-center justify-center gap-1">
                            <div className="w-[35px] text-[12px] font-medium text-[#666] text-[#8A98A3]">In</div>
                            <MultiSelectWithSearch
                                mode="multiple"
                                placeholder="Select diagnoses..."
                                value={inValues.map(i => i.value)}
                                onChange={values => handleValueSelect(values, true, 'select')}
                                options={options}
                                style={{ width: '100%' }}
                                className={Styles.filterSelectbox}
                                onSearch={handleSearch}
                                requiresValues={meta.requiresValues}
                                loading={!values && meta.requiresValues}
                                notFoundContent={!values && meta.requiresValues ? 'Loading...' : 'No options found'}
                                getLabelForValue={getLabelForValue}
                                searchPlaceholder="Search diagnoses..."
                                dropdownRender={menu => {
                                    if (groupedOptions.length > 0) {
                                        // Convert icdValues to Tree keys for checkedKeys
                                        // values is now {value, title} format, extract values
                                        const findKeysForValues = (values: any[], nodes: any[]): React.Key[] => {
                                            const valueArray = values.map(v =>
                                                typeof v === 'object' && v.value !== undefined ? v.value : v
                                            );
                                            const keys: React.Key[] = [];
                                            const traverse = (nodeList: any[]) => {
                                                nodeList.forEach(node => {
                                                    // Only process leaf nodes with icdValue
                                                    if (
                                                        node.isLeaf === true &&
                                                        node.icdValue &&
                                                        valueArray.includes(node.icdValue)
                                                    ) {
                                                        keys.push(node.key);
                                                    }
                                                    // Recursively process children
                                                    if (node.children && Array.isArray(node.children)) {
                                                        traverse(node.children);
                                                    }
                                                });
                                            };
                                            traverse(nodes);
                                            return keys;
                                        };

                                        const checkedKeys = findKeysForValues(inValues, inTreeData);

                                        return (
                                            <div
                                                className={Styles.diagnosisTreeWrapper}
                                                style={{ maxHeight: 300, overflowY: 'auto', fontSize: '12px' }}
                                            >
                                                <Tree
                                                    treeData={inTreeData}
                                                    checkable
                                                    checkedKeys={checkedKeys}
                                                    onCheck={checkedKeys => {
                                                        const checked = Array.isArray(checkedKeys)
                                                            ? checkedKeys
                                                            : checkedKeys.checked || [];
                                                        const selectedValues: any[] = [];

                                                        // Find values for checked keys - only process leaf nodes
                                                        const findValues = (keys: React.Key[], nodes: any[]) => {
                                                            const traverse = (nodeList: any[]) => {
                                                                nodeList.forEach(node => {
                                                                    // Only process leaf nodes with icdValue
                                                                    if (
                                                                        node.isLeaf === true &&
                                                                        node.icdValue &&
                                                                        keys.includes(node.key)
                                                                    ) {
                                                                        selectedValues.push(node.icdValue);
                                                                    }
                                                                    // Recursively process children
                                                                    if (node.children && Array.isArray(node.children)) {
                                                                        traverse(node.children);
                                                                    }
                                                                });
                                                            };
                                                            traverse(nodes);
                                                        };

                                                        findValues(checked, inTreeData);

                                                        handleValueSelect(selectedValues, true, 'tree');
                                                    }}
                                                    showLine={{ showLeafIcon: false }}
                                                    style={{ padding: '8px 0', fontSize: '12px' }}
                                                />
                                            </div>
                                        );
                                    }
                                    return menu;
                                }}
                            />
                        </div>

                        {/* Out (OUT) Selectbox */}
                        <div className="flex items-center justify-center gap-1">
                            <div className="w-[35px] text-[12px] font-medium text-[#666] text-[#8A98A3]">Out</div>
                            <MultiSelectWithSearch
                                mode="multiple"
                                placeholder="Select diagnoses..."
                                value={outValues.map(o =>
                                    typeof o === 'object' && o.value !== undefined ? o.value : o
                                )}
                                onChange={values => handleValueSelect(values, false, 'select')}
                                options={options}
                                style={{ width: '100%' }}
                                className={Styles.filterSelectbox}
                                onSearch={handleSearch}
                                requiresValues={meta.requiresValues}
                                loading={!values && meta.requiresValues}
                                notFoundContent={!values && meta.requiresValues ? 'Loading...' : 'No options found'}
                                getLabelForValue={getLabelForValue}
                                searchPlaceholder="Search diagnoses..."
                                dropdownRender={menu => {
                                    if (groupedOptions.length > 0) {
                                        // Convert icdValues to Tree keys for checkedKeys
                                        // values is now {value, title} format, extract values
                                        const findKeysForValues = (values: any[], nodes: any[]): React.Key[] => {
                                            const valueArray = values.map(v =>
                                                typeof v === 'object' && v.value !== undefined ? v.value : v
                                            );
                                            const keys: React.Key[] = [];
                                            const traverse = (nodeList: any[]) => {
                                                nodeList.forEach(node => {
                                                    // Only process leaf nodes with icdValue
                                                    if (
                                                        node.isLeaf === true &&
                                                        node.icdValue &&
                                                        valueArray.includes(node.icdValue)
                                                    ) {
                                                        keys.push(node.key);
                                                    }
                                                    // Recursively process children
                                                    if (node.children && Array.isArray(node.children)) {
                                                        traverse(node.children);
                                                    }
                                                });
                                            };
                                            traverse(nodes);
                                            return keys;
                                        };

                                        const checkedKeys = findKeysForValues(outValues, outTreeData);

                                        return (
                                            <div
                                                className={Styles.diagnosisTreeWrapper}
                                                style={{ maxHeight: 300, overflowY: 'auto', fontSize: '12px' }}
                                            >
                                                <Tree
                                                    treeData={outTreeData}
                                                    checkable
                                                    checkedKeys={checkedKeys}
                                                    onCheck={checkedKeys => {
                                                        const checked = Array.isArray(checkedKeys)
                                                            ? checkedKeys
                                                            : checkedKeys.checked || [];
                                                        const selectedValues: any[] = [];

                                                        // Find values for checked keys - only process leaf nodes
                                                        const findValues = (keys: React.Key[], nodes: any[]) => {
                                                            const traverse = (nodeList: any[]) => {
                                                                nodeList.forEach(node => {
                                                                    // Only process leaf nodes with icdValue
                                                                    if (
                                                                        node.isLeaf === true &&
                                                                        node.icdValue &&
                                                                        keys.includes(node.key)
                                                                    ) {
                                                                        selectedValues.push(node.icdValue);
                                                                    }
                                                                    // Recursively process children
                                                                    if (node.children && Array.isArray(node.children)) {
                                                                        traverse(node.children);
                                                                    }
                                                                });
                                                            };
                                                            traverse(nodes);
                                                        };

                                                        findValues(checked, outTreeData);

                                                        handleValueSelect(selectedValues, false, 'tree');
                                                    }}
                                                    showLine={{ showLeafIcon: false }}
                                                    style={{ padding: '8px 0', fontSize: '12px' }}
                                                />
                                            </div>
                                        );
                                    }
                                    return menu;
                                }}
                            />
                        </div>
                    </div>
                );
            }

            // For other MULTI_SELECT fields, use two simple selectboxes
            // Disable options that are selected in the other selectbox
            // inValues and outValues are now {value, title} format, extract values
            const outValueArray = outValues.map(v => (typeof v === 'object' && v.value !== undefined ? v.value : v));
            const inValueArray = inValues.map(v => (typeof v === 'object' && v.value !== undefined ? v.value : v));
            const inOptions = options.map((opt: any) => ({
                ...opt,
                disabled: outValueArray.includes(opt.value),
            }));
            const outOptions = options.map((opt: any) => ({
                ...opt,
                disabled: inValueArray.includes(opt.value),
            }));

            return (
                <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 4 }}>
                    {/* In (IN) Selectbox */}
                    <div className="flex items-center justify-center gap-1">
                        <div className="w-[35px] text-[12px] font-medium text-[#666] text-[#8A98A3]">In</div>
                        <MultiSelectWithSearch
                            mode="multiple"
                            placeholder={`Select ${meta.label.toLowerCase()}...`}
                            value={inValues.map(i => i.value)}
                            onChange={values => handleValueSelect(values, true)}
                            options={inOptions}
                            style={{ width: '100%' }}
                            className={Styles.filterSelectbox}
                            onSearch={meta.requiresValues ? handleSearch : undefined}
                            requiresValues={meta.requiresValues}
                            loading={!values && meta.requiresValues}
                            notFoundContent={!values && meta.requiresValues ? 'Loading...' : 'No options found'}
                            getLabelForValue={getLabelForValue}
                            searchPlaceholder={`Search ${meta.label.toLowerCase()}...`}
                        />
                    </div>

                    {/* Out (OUT) Selectbox */}
                    <div className="flex items-center justify-center gap-1">
                        <div className="w-[35px] text-[12px] font-medium text-[#666] text-[#8A98A3]">Out</div>
                        <MultiSelectWithSearch
                            mode="multiple"
                            placeholder={`Select ${meta.label.toLowerCase()}...`}
                            value={outValues.map(o => o.value)}
                            onChange={values => handleValueSelect(values, false)}
                            options={outOptions}
                            style={{ width: '100%' }}
                            className={Styles.filterSelectbox}
                            onSearch={meta.requiresValues ? handleSearch : undefined}
                            requiresValues={meta.requiresValues}
                            loading={!values && meta.requiresValues}
                            notFoundContent={!values && meta.requiresValues ? 'Loading...' : 'No options found'}
                            getLabelForValue={getLabelForValue}
                            searchPlaceholder={`Search ${meta.label.toLowerCase()}...`}
                        />
                    </div>
                </div>
            );
        }

        // For single SELECT, show normally
        return (
            <Select
                placeholder={`Select ${meta.label.toLowerCase()}`}
                value={condition.value}
                onChange={value => {
                    updateCondition(groupId, condition.id, {
                        value,
                        operator: mapDefaultOperator(meta.defaultOperator) || (Array.isArray(value) ? 'in' : 'equals'),
                    });
                }}
                options={options}
                style={{ width: '100%' }}
                className={cs(Styles.filterSelectbox, Styles.customSelect)}
                showSearch
                allowClear
                loading={!values && meta.requiresValues}
                notFoundContent={!values && meta.requiresValues ? 'Loading...' : 'No options'}
                onSearch={meta.requiresValues ? handleSearch : undefined}
                filterOption={
                    meta.requiresValues
                        ? false
                        : (input, option) =>
                              ((option?.label as string) ?? '').toLowerCase().includes(input.toLowerCase())
                }
            />
        );
    }

    if (meta.inputType === FilterInputType.BOOLEAN) {
        // Represent boolean with radio buttons
        // Backend expects 'exists' or 'not_exists' operators for boolean filters
        return (
            <Radio.Group
                value={condition.value}
                onChange={e => {
                    const val = e.target.value;
                    updateCondition(groupId, condition.id, {
                        value: val,
                        operator: (val ? 'exists' : 'not_exists') as ConditionOperator,
                    });
                }}
                className={Styles.booleanRadioGroup}
                style={{ width: '100%', display: 'flex', gap: '16px' }}
            >
                <Radio
                    value={true}
                    className={Styles.booleanRadio}
                >
                    Yes
                </Radio>
                <Radio
                    value={false}
                    className={Styles.booleanRadio}
                >
                    No
                </Radio>
            </Radio.Group>
        );
    }

    if (meta.inputType === FilterInputType.DATE_RANGE) {
        // Convert string dates to dayjs objects for RangePicker
        const rangeValue: [Dayjs | null, Dayjs | null] | null =
            Array.isArray(condition.value) && condition.value.length === 2 && condition.value[0] && condition.value[1]
                ? [dayjs(condition.value[0]), dayjs(condition.value[1])]
                : null;

        // Build tooltip text for selected dates
        const tooltipTitle =
            rangeValue && rangeValue[0] && rangeValue[1]
                ? `${rangeValue[0].format('YYYY-MM-DD')} - ${rangeValue[1].format('YYYY-MM-DD')}`
                : undefined;

        return (
            <Form.Item style={{ marginBottom: 0, flex: 1 }}>
                <Tooltip
                    title={tooltipTitle}
                    placement="top"
                >
                    <div>
                        <DatePicker.RangePicker
                            value={rangeValue}
                            onChange={dates => {
                                if (dates && dates[0] && dates[1]) {
                                    updateCondition(groupId, condition.id, {
                                        value: [dates[0].format('YYYY-MM-DD'), dates[1].format('YYYY-MM-DD')],
                                        operator: 'between',
                                    });
                                } else {
                                    updateCondition(groupId, condition.id, {
                                        value: undefined,
                                        operator: 'between',
                                    });
                                }
                            }}
                            style={{ width: '100%' }}
                            className={Styles.dateRangePicker}
                            format="YYYY-MM-DD"
                        />
                    </div>
                </Tooltip>
            </Form.Item>
        );
    }

    if (meta.inputType === FilterInputType.DOC_RANKER) {
        return (
            <DocRankerConditionInput
                condition={condition as any}
                groupId={groupId}
                updateCondition={(gid, cid, updates) => updateCondition(gid, cid, { ...updates, operator: 'equals' })}
            />
        );
    }

    return null;
}
